/**
 * RecorderPage
 * Minimal standalone page for recording in a popup window.
 * Used when the main widget is inside a ServiceNow iframe (which blocks mic access).
 * Communicates back to the parent window via postMessage.
 * Includes mid-call ticket update controls.
 *
 * WS2: Dual-quality (PSA + Whisper) with multipart upload
 * WS6: IndexedDB recovery banner
 * WS7: Two-way capture with loopback detection
 */

import { useState, useEffect, useRef } from 'react'
import { Mic, Monitor, PhoneCall, AlertCircle, CheckCircle2, Info, Phone, Square, Pause, Play, X, Loader2, Upload, FileText, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCallRecordingContext, formatDuration, type AudioCaptureMode } from '@/contexts/CallRecordingContext'
import { useRecordingStatus, useRecordingFlow } from '@/hooks/useCallRecordings'
import { useInterimUpdates, type AutoUpdateInterval } from '@/hooks/useInterimUpdates'
import { useAudioDevices } from '@/hooks/useAudioDevices'
import { useDarkMode } from '@/hooks/useDarkMode'
import { getAudioFormat } from '@/hooks/useCallRecording'
import { cn } from '@/lib/utils'
import { createSession, hasValidSession } from '@/services/api'

function getTimeSince(date: Date | null): string {
  if (!date) return ''
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ago`
}

export function RecorderPage() {
  const params = new URLSearchParams(window.location.search)
  const sysId = params.get('sys_id') || undefined
  const table = params.get('table') || undefined

  // Sync dark mode from URL param before useDarkMode reads localStorage.
  const darkParam = params.get('dark')
  if (darkParam !== null) {
    localStorage.setItem('snpod-dark-mode', darkParam === '1' ? 'true' : 'false')
  }

  useDarkMode()

  // Create session on mount so API calls are authenticated
  const sessionCreatingRef = useRef(false)

  useEffect(() => {
    if (hasValidSession()) return
    const userId = params.get('user_id')
    const userName = params.get('user_name')
    if (!userId || sessionCreatingRef.current) return

    sessionCreatingRef.current = true
    createSession({
      userId,
      userName: userName || undefined,
      ticketSysId: sysId,
      table,
    })
      .then(() => console.log('[RecorderPage] Session created'))
      .catch((err) => console.error('[RecorderPage] Session creation failed:', err))
      .finally(() => { sessionCreatingRef.current = false })
  }, [])

  const {
    isRecording, isPaused, duration, audioLevel, status, recordingId, audioBlob, whisperBlob,
    startRecording, stopRecording, pauseRecording, resumeRecording, cancelRecording,
    setStatus, setRecordingId, setError,
    isMicrophoneSupported, isSystemAudioSupported, error,
    recoverableSession, recoverSession, dismissRecovery,
  } = useCallRecordingContext()

  const interim = useInterimUpdates(sysId || null, table || null)

  const [captureMode, setCaptureMode] = useState<AudioCaptureMode>('microphone')
  const [selectedSpeakerDeviceId, setSelectedSpeakerDeviceId] = useState<string | undefined>(undefined)
  const [isStarting, setIsStarting] = useState(false)
  const [uploadStarted, setUploadStarted] = useState(false)
  // Gate Start Recording until the device scan probe has fully released its stream.
  // The scan() in useAudioDevices calls getUserMedia then immediately stops all tracks.
  // On virtual devices like VB-Cable the hardware needs a brief moment to fully release
  // before a second getUserMedia can acquire it with a live (non-silent) track.
  // isScanning goes false only after getTracks().stop() has been called, so waiting
  // for !isScanning gives the device ~one event-loop tick to settle.
  const { devices, loopbackDevice, hasLoopback, isScanning } = useAudioDevices()
  const [deviceScanComplete, setDeviceScanComplete] = useState(false)
  useEffect(() => {
    if (!isScanning) {
      // Small delay to let the virtual device fully release the hardware after the probe
      const t = setTimeout(() => setDeviceScanComplete(true), 150)
      return () => clearTimeout(t)
    }
  }, [isScanning])

  // Auto-select loopback device when detected
  useEffect(() => {
    if (loopbackDevice) {
      setSelectedSpeakerDeviceId(loopbackDevice.deviceId)
    }
  }, [loopbackDevice])

  // Force re-render for "time since" display
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!interim.lastUpdateTime) return
    const id = setInterval(() => setTick(t => t + 1), 30000)
    return () => clearInterval(id)
  }, [interim.lastUpdateTime])

  const { uploadAndProcessMultipart } = useRecordingFlow()

  // Auto-upload when recording stops
  useEffect(() => {
    if (audioBlob && status === 'uploading' && !uploadStarted && !recordingId) {
      setUploadStarted(true)
      handleUpload()
    }
  }, [audioBlob, status, uploadStarted, recordingId])

  useEffect(() => { if (status === 'idle') setUploadStarted(false) }, [status])

  const handleUpload = async () => {
    if (!audioBlob) return
    try {
      const format = getAudioFormat(audioBlob.type)
      const id = await uploadAndProcessMultipart({
        audioBlob,
        whisperBlob: whisperBlob ?? undefined,
        audioFormat: format,
        durationSeconds: duration,
        sysId: sysId ?? undefined,
        table: table ?? undefined,
      })
      setRecordingId(id)
      setStatus('processing')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      setStatus('error')
    }
  }

  // Poll processing status
  const { data: statusData } = useRecordingStatus(
    status === 'processing' && recordingId ? recordingId : null,
    { pollingInterval: 2000 }
  )

  useEffect(() => {
    if (statusData?.status === 'completed') {
      setStatus('complete')
      // Notify parent window
      if (window.opener) {
        window.opener.postMessage({
          type: 'snpod-recording-complete',
          recordingId,
        }, '*')
      }
    } else if (statusData?.status === 'failed') {
      setError(statusData.errorMessage || 'Processing failed')
      setStatus('error')
    }
  }, [statusData, setStatus, setError, recordingId])

  const handleStartRecording = async () => {
    try {
      setIsStarting(true)
      const speakerDeviceId = captureMode === 'twoway' ? selectedSpeakerDeviceId : undefined
      await startRecording(captureMode, sysId, table, undefined, speakerDeviceId)
      setIsStarting(false)
    } catch (err) {
      setIsStarting(false)
    }
  }

  const handleStopRecording = async () => { await stopRecording() }

  const handleCancel = () => {
    interim.resetInterimState()
    cancelRecording()
    if (window.opener) {
      window.opener.postMessage({ type: 'snpod-recording-cancelled' }, '*')
    }
    window.close()
  }

  const handleClose = () => {
    if (window.opener) {
      window.opener.postMessage({
        type: 'snpod-recording-complete',
        recordingId,
      }, '*')
    }
    window.close()
  }

  const isProcessing = status === 'uploading' || status === 'processing'
  const processingLabel = status === 'uploading' ? 'Uploading...'
    : statusData?.status === 'transcribing' ? 'Transcribing...'
    : statusData?.status === 'generating_notes' ? 'Generating notes...'
    : 'Processing...'
  const processingProgress = statusData?.progress || (status === 'uploading' ? 20 : 40)

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 flex flex-col">
      {/* Header */}
      <div className={cn(
        'flex items-center justify-between px-4 py-3',
        isRecording && !isPaused ? 'bg-red-500' :
        isPaused ? 'bg-yellow-500' :
        isProcessing ? 'bg-blue-500' :
        status === 'complete' ? 'bg-green-500' :
        'bg-blue-600'
      )}>
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-white" />
          <span className="text-white font-medium text-sm">
            {isRecording ? (isPaused ? 'Paused' : 'Recording') :
             isProcessing ? processingLabel :
             status === 'complete' ? 'Complete' :
             'Record Call'}
          </span>
        </div>
        {!isRecording && !isProcessing && (
          <button onClick={handleCancel} className="text-white/80 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex-1 p-4 flex flex-col">
        {/* Idle state - show consent and start */}
        {status === 'idle' && (
          <div className="flex-1 flex flex-col gap-3">
            {/* Recovery banner (WS6) */}
            {recoverableSession && (
              <div className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-900/30 rounded-md text-xs">
                <div className="flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>Unsaved recording found</span>
                </div>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={recoverSession}>
                    Recover
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-gray-500" onClick={dismissRecovery}>
                    Discard
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-md text-xs text-blue-800 dark:text-blue-300">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>This call will be recorded and transcribed. Ensure all parties consent.</p>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium dark:text-slate-200">Audio Source</label>

              <label className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-xs dark:text-slate-200 ${captureMode === 'microphone' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-slate-600'} ${!isMicrophoneSupported ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input type="radio" name="captureMode" value="microphone" checked={captureMode === 'microphone'} onChange={() => setCaptureMode('microphone')} disabled={!isMicrophoneSupported} className="shrink-0" />
                <Mic className="h-3.5 w-3.5 shrink-0" />
                <div><span className="font-medium">Microphone</span><span className="text-gray-500 dark:text-slate-400 ml-1">- Your voice only</span></div>
              </label>

              <label className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-xs dark:text-slate-200 ${captureMode === 'twoway' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-slate-600'} ${!isMicrophoneSupported ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input type="radio" name="captureMode" value="twoway" checked={captureMode === 'twoway'} onChange={() => setCaptureMode('twoway')} disabled={!isMicrophoneSupported} className="shrink-0" />
                <PhoneCall className="h-3.5 w-3.5 shrink-0" />
                <div className="flex items-center gap-1.5">
                  <span className="font-medium">Mic + Speaker (Two-Way)</span>
                  {hasLoopback && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full">Ready</span>}
                </div>
              </label>

              {/* Two-way details */}
              {captureMode === 'twoway' && (
                <div className="pl-7 space-y-2">
                  {hasLoopback ? (
                    <div className="flex items-center gap-1.5 text-[10px] text-green-600 dark:text-green-400">
                      <CheckCircle2 className="h-3 w-3" />
                      Loopback device detected — zero dialogs
                    </div>
                  ) : (
                    <div className="flex items-start gap-1.5 text-[10px] text-gray-500 dark:text-slate-400">
                      <Info className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>Requires screen share permission. Install <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">VB-Cable</a> for one-click recording.</span>
                    </div>
                  )}
                  {/* Device picker override */}
                  {devices.length > 1 && (
                    <select
                      value={selectedSpeakerDeviceId || ''}
                      onChange={(e) => setSelectedSpeakerDeviceId(e.target.value || undefined)}
                      className="w-full h-7 text-[10px] border border-gray-300 dark:border-slate-600 rounded px-2 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-300"
                    >
                      <option value="">Screen share (default)</option>
                      {devices.filter(d => d.deviceId !== 'default').map(d => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label}{d.isLoopback ? ' (loopback)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <label className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-xs dark:text-slate-200 ${captureMode === 'system' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-slate-600'} ${!isSystemAudioSupported ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input type="radio" name="captureMode" value="system" checked={captureMode === 'system'} onChange={() => setCaptureMode('system')} disabled={!isSystemAudioSupported} className="shrink-0" />
                <Monitor className="h-3.5 w-3.5 shrink-0" />
                <div><span className="font-medium">System Audio</span><span className="text-gray-500 dark:text-slate-400 ml-1">- Softphone</span>{!isSystemAudioSupported && <span className="text-red-500 ml-1">N/A</span>}</div>
              </label>
            </div>

            {error && (
              <div className="flex gap-2 p-2 bg-red-50 dark:bg-red-900/30 rounded-md text-xs text-red-800 dark:text-red-300">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-auto">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={isStarting}>Cancel</Button>
              <Button size="sm" onClick={handleStartRecording} disabled={isStarting || !deviceScanComplete} className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">
                <Mic className="h-3.5 w-3.5" />
                {isStarting ? 'Starting...' : !deviceScanComplete ? 'Preparing...' : 'Start Recording'}
              </Button>
            </div>
          </div>
        )}

        {/* Recording state */}
        {isRecording && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="flex items-center gap-3">
              <div className={cn('w-3 h-3 rounded-full', isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse')} />
              <span className="font-mono text-3xl font-semibold text-gray-900 dark:text-white">{formatDuration(duration)}</span>
            </div>

            <div className="flex items-center gap-2 w-full max-w-[200px]">
              {isPaused ? <Mic className="h-4 w-4 text-yellow-500" /> : <Mic className={cn('h-4 w-4', audioLevel > 0.1 ? 'text-green-500' : 'text-gray-400')} />}
              <div className="flex-1 h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                <div className={cn('h-full rounded-full transition-all duration-100', isPaused ? 'bg-yellow-500' : 'bg-green-500')} style={{ width: `${Math.min(100, audioLevel * 100)}%` }} />
              </div>
            </div>

            <div className="flex items-center gap-2 w-full mt-4">
              <Button variant="outline" size="sm" onClick={() => isPaused ? resumeRecording() : pauseRecording()} className="flex-1">
                {isPaused ? <><Play className="h-4 w-4 mr-1" />Resume</> : <><Pause className="h-4 w-4 mr-1" />Pause</>}
              </Button>
              <Button size="sm" onClick={handleStopRecording} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                <Square className="h-4 w-4 mr-1" />Stop
              </Button>
            </div>

            {/* Interim update controls */}
            {sysId && (
              <div className="w-full space-y-2 pt-2 border-t border-gray-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => interim.triggerUpdate()}
                    disabled={interim.isUpdating || duration < 5}
                    className="flex-1 text-xs"
                  >
                    {interim.isUpdating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                    Update Ticket
                  </Button>
                  <select
                    value={interim.autoUpdateInterval}
                    onChange={(e) => interim.setAutoUpdateInterval(Number(e.target.value) as AutoUpdateInterval)}
                    className={cn(
                      'h-8 text-xs border rounded-md px-2 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-300 dark:border-slate-600 cursor-pointer',
                      interim.autoUpdateInterval > 0 && 'border-green-500 dark:border-green-500 text-green-700 dark:text-green-400'
                    )}
                  >
                    <option value={0}>Auto: Off</option>
                    <option value={5}>Auto: 5m</option>
                    <option value={15}>Auto: 15m</option>
                    <option value={30}>Auto: 30m</option>
                  </select>
                </div>
                {interim.isUpdating && (
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 text-center">Updating ticket...</p>
                )}
                {!interim.isUpdating && interim.lastUpdateTime && (
                  <p className="text-[10px] text-green-600 dark:text-green-400 text-center">
                    Updated {getTimeSince(interim.lastUpdateTime)} (#{interim.lastUpdateNumber})
                  </p>
                )}
                {interim.error && !interim.isUpdating && (
                  <p className="text-[10px] text-red-500 dark:text-red-400 text-center">{interim.error}</p>
                )}
              </div>
            )}

            <Button variant="ghost" size="sm" onClick={handleCancel} className="text-gray-500 dark:text-slate-400 hover:text-red-500">
              <X className="h-4 w-4 mr-1" />Cancel
            </Button>
          </div>
        )}

        {/* Processing state */}
        {isProcessing && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              {status === 'uploading' ? <Upload className="h-6 w-6 text-blue-500" /> : <FileText className="h-6 w-6 text-blue-500" />}
            </div>
            <p className="text-sm text-gray-600 dark:text-slate-300 text-center">{processingLabel}</p>
            <div className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${processingProgress}%` }} />
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Recording: {formatDuration(duration)}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 italic">Processing your recording...</p>
          </div>
        )}

        {/* Complete state */}
        {status === 'complete' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            </div>
            <p className="text-sm font-medium text-green-700 dark:text-green-400">Recording processed successfully!</p>
            <p className="text-xs text-gray-500 dark:text-slate-400">You can close this window and view notes in the Recording tab.</p>
            <Button size="sm" onClick={handleClose} className="bg-green-600 hover:bg-green-700 text-white">
              Done
            </Button>
          </div>
        )}

        {/* Error state */}
        {status === 'error' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <p className="text-sm font-medium text-red-700 dark:text-red-400">Something went wrong</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 text-center">{error || 'An unexpected error occurred'}</p>
            <Button variant="outline" size="sm" onClick={handleCancel}>Close</Button>
          </div>
        )}
      </div>
    </div>
  )
}
